import { AppStreamSDK } from "../../../../utils/AppStreamSDK";
import { useTranslation } from "react-i18next";
import { useFilesNotificationStore } from "../../../../hooks/useNotificationStore";
import { IFileExplorerApi, NotificationItem } from "@amzn/aws-euc-ui";
import { useNotification } from "../notification/useNotification";
import { useMemo } from "react";
import { EmbedConstants } from "@amzn/photon-portal-embed-sdk";
import log, { TOOLBAR_METRIC_NAME } from "../../../../logging";
import { ABORTED_AS2_LISTFILE_MSG } from "../../../../constants/Toolbar";

// https://pubs.opengroup.org/onlinepubs/007908799/xbd/glossary.html#tag_004_000_114
const INVALID_NAME_CHARS = ["/", "\0"];

export const useFilesApis = (appStreamSDK: AppStreamSDK) => {
    const { t } = useTranslation();

    const notificationList = useFilesNotificationStore(
        (store) => store.notificationList
    );

    const addNotification = useFilesNotificationStore(
        (store) => store.addNotification
    );

    const removeNotification = useFilesNotificationStore(
        (store) => store.removeNotification
    );

    const DELETE_FILE_ERROR: NotificationItem = useNotification({
        header: t("notification.fail.deleteFile.content"),
        removeNotification: removeNotification,
    });

    const LIST_FILE_ERROR: NotificationItem = useNotification({
        header: t("notification.fail.listFile.content"),
        removeNotification: removeNotification,
    });

    const RENAME_DIRECTORY_ERROR: NotificationItem = useNotification({
        header: t("notification.fail.renameDirectory.content"),
        removeNotification: removeNotification,
    });

    const DOWNLOAD_FILE_ERROR: NotificationItem = useNotification({
        header: t("notification.fail.downloadFile.content"),
        removeNotification: removeNotification,
    });

    const UPLOAD_FILE_ERROR: NotificationItem = useNotification({
        header: t("notification.fail.uploadFile.content"),
        removeNotification: removeNotification,
    });

    return useMemo<IFileExplorerApi>(
        () => ({
            // CreateDirectory error handled internally: https://tiny.amazon.com/107hpu5e4/codeamazpackAWSEblob865dsrc
            createDirectory: (path) => {
                return appStreamSDK
                    .performActionPromise(EmbedConstants.METHOD_CREATE_FOLDER, {
                        remotePath: path,
                    })
                    .then((result) => {
                        log.publishCounterMetric(
                            TOOLBAR_METRIC_NAME.CREATE_DIRECTORY_SUCCESS
                        );
                        return result;
                    })
                    .catch((error) => {
                        log.publishCounterMetric(
                            TOOLBAR_METRIC_NAME.CREATE_DIRECTORY_ERROR
                        );
                        throw error;
                    });
            },
            deleteFile: (path) =>
                appStreamSDK
                    .performActionPromise(EmbedConstants.METHOD_DELETE_FILE, {
                        remotePath: path,
                    })
                    .then((result) => {
                        log.publishCounterMetric(
                            TOOLBAR_METRIC_NAME.DELETE_FILE_SUCCESS
                        );
                        return result;
                    })
                    .catch((error) => {
                        log.publishCounterMetric(
                            TOOLBAR_METRIC_NAME.DELETE_FILE_ERROR
                        );
                        addNotification(DELETE_FILE_ERROR);
                    }),
            list: (path) =>
                appStreamSDK
                    .listFiles(path)
                    .then((result) => {
                        log.publishCounterMetric(
                            TOOLBAR_METRIC_NAME.LIST_FILES_SUCCESS
                        );
                        return result;
                    })
                    .catch((error) => {
                        // The in-session root folder path is /TemporaryFiles. The files toolbar item fetches the data from / first by default
                        // and sends a new request to get the file list under /TemporaryFiles. This is causing a "The operation was aborted" error,
                        // which is expected. Since this is expected behavior, we will ignore the error to reduce noise on the dashboard.
                        if (
                            path === "/" &&
                            error.errorMessage === ABORTED_AS2_LISTFILE_MSG
                        ) {
                            log.logMessage(
                                `Initial FileExplorer.ListFiles API call pointing to the "/" folder is aborted.`
                            );
                            return;
                        }
                        log.publishCounterMetric(
                            TOOLBAR_METRIC_NAME.LIST_FILES_ERROR
                        );
                        // https://sim.amazon.com/issues/LOWA-9174
                        if (error.errorCode !== 400) {
                            // Only show ListFile API error once since we are using 3 retries with useQuery (to avoid multiple errors on screen from one fetch)
                            if (
                                !notificationList.find(
                                    (item) =>
                                        item.header === LIST_FILE_ERROR.header
                                )
                            ) {
                                addNotification(LIST_FILE_ERROR);
                            }
                        }
                        return [];
                    }),
            renameDirectory: (src, dest) =>
                appStreamSDK
                    .performActionPromise(EmbedConstants.METHOD_RENAME_FILE, {
                        remotePath: src,
                        newPath: dest,
                    })
                    .then((result) => {
                        log.publishCounterMetric(
                            TOOLBAR_METRIC_NAME.RENAME_DIRECTORY_SUCCESS
                        );
                        return result;
                    })
                    .catch((error) => {
                        log.publishCounterMetric(
                            TOOLBAR_METRIC_NAME.RENAME_DIRECTORY_ERROR
                        );
                        addNotification(RENAME_DIRECTORY_ERROR);
                    }),
            // RenameFile error handled internally: https://tiny.amazon.com/oxaj7lvc/codeamazpackAWSEblob865dsrc
            renameFile: (originalPath, newPath) =>
                appStreamSDK
                    .performActionPromise(EmbedConstants.METHOD_RENAME_FILE, {
                        remotePath: originalPath,
                        newPath: newPath,
                    })
                    .then((result) => {
                        log.publishCounterMetric(
                            TOOLBAR_METRIC_NAME.RENAME_FILE_SUCCESS
                        );
                        return result;
                    })
                    .catch((error) => {
                        log.publishCounterMetric(
                            TOOLBAR_METRIC_NAME.RENAME_FILE_ERROR
                        );
                        throw error;
                    }),
            retrieveFile: (path) =>
                appStreamSDK
                    .performActionPromise(EmbedConstants.METHOD_DOWNLOAD_FILE, {
                        remotePath: path,
                    })
                    .then((result) => {
                        log.publishCounterMetric(
                            TOOLBAR_METRIC_NAME.DOWNLOAD_FILE_SUCCESS
                        );
                        return result;
                    })
                    .catch((error) => {
                        log.publishCounterMetric(
                            TOOLBAR_METRIC_NAME.DOWNLOAD_FILE_ERROR
                        );
                        addNotification(DOWNLOAD_FILE_ERROR);
                    }),
            storeFile: async (file, dir, onProgress) => {
                onProgress(0);

                try {
                    await appStreamSDK.performActionPromise(
                        EmbedConstants.METHOD_UPLOAD_FILE,
                        {
                            file: file,
                            remotePath: dir,
                        }
                    );
                    log.publishCounterMetric(
                        TOOLBAR_METRIC_NAME.UPLOAD_FILE_SUCCESS
                    );
                } catch (error) {
                    log.publishCounterMetric(
                        TOOLBAR_METRIC_NAME.UPLOAD_FILE_ERROR
                    );
                    addNotification(UPLOAD_FILE_ERROR);
                }

                onProgress(file.size);
            },
            validateName: (name) => {
                if (
                    INVALID_NAME_CHARS.some((invalidChar) =>
                        name.includes(invalidChar)
                    )
                ) {
                    return t(
                        "toolbar.item.files.name.validation.invalidCharacter"
                    );
                }
                return null;
            },
        }),
        [appStreamSDK, JSON.stringify(notificationList)]
    );
};
